import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Plus, User, Trash2, BriefcaseBusiness, Mic } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { fetchLatestScores } from '@/lib/personaScore';
import { PersonaScoreBadge } from '@/components/PersonaScoreBadge';

const Vendedor = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState(0);
  const [personas, setPersonas] = useState<any[]>([]);
  const [personaScores, setPersonaScores] = useState<Record<string, number>>({});
  const [empresaIdForNav, setEmpresaIdForNav] = useState<string | null>(null);

  useEffect(() => {
    const loadUserData = async () => {
      if (authLoading) return;
      
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const { data: usuario, error: userErr } = await supabase
          .from('usuarios')
          .select('papel, empresa_id')
          .eq('id', user.id)
          .single();

        if (userErr) {
          console.error(userErr);
          navigate('/dashboard');
          return;
        }

        if (!usuario || usuario.papel !== 'vendedor') {
          navigate('/dashboard');
          return;
        }

        if (!usuario.empresa_id) {
          navigate('/briefing');
          return;
        }

        setEmpresaIdForNav(usuario.empresa_id);

        // Buscar feedbacks da empresa
        const { data: empresaData, error: empErr } = await supabase
          .from('empresas')
          .select('feedbacks')
          .eq('id', usuario.empresa_id)
          .single();

        if (empErr) {
          console.error(empErr);
        } else if (empresaData) {
          setFeedbacks(empresaData.feedbacks || 0);
        }

        // Buscar personas da empresa
        const { data: personasData, error: perErr } = await supabase
          .from('personas')
          .select('*')
          .eq('empresa_id', usuario.empresa_id)
          .order('created_at', { ascending: false });

        if (perErr) {
          console.error(perErr);
        } else if (personasData) {
          setPersonas(personasData);
          const scores = await fetchLatestScores(personasData.map((p: any) => p.id));
          setPersonaScores(scores);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user, authLoading, navigate]);

  const handleDeletePersona = async (personaId: string) => {
    try {
      const { error } = await supabase.from('personas').delete().eq('id', personaId);

      if (error) throw error;

      toast({
        title: 'Persona excluída',
        description: 'A persona foi excluída com sucesso.',
      });

      // Atualizar lista de personas localmente
      setPersonas((prev) => prev.filter(p => p.id !== personaId));
    } catch (error) {
      console.error('Erro ao excluir persona:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir a persona.',
        variant: 'destructive',
      });
    }
  };

  const handleStartTraining = () => {
    if (!empresaIdForNav) {
      toast({
        title: 'Empresa não encontrada',
        description: 'Tente novamente abrindo o treinamento a partir do painel.',
        variant: 'destructive',
      });
      return;
    }
    navigate(`/treinamento?empresaId=${empresaIdForNav}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Layout title="Minhas Personas">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Personas de Vendas</h1>
            <p className="text-muted-foreground mt-2">
              Crie personas e pratique suas habilidades de vendas
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Feedbacks disponíveis</p>
            <p className="text-2xl font-bold text-primary">{feedbacks}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-card to-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Pronto para treinar?
              </CardTitle>
              <CardDescription>
                Inicie uma sessão de treinamento com IA para melhorar suas técnicas de vendas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleStartTraining}
                className="w-full"
                size="lg"
                disabled={!empresaIdForNav}
              >
                <Plus className="w-4 h-4 mr-2" />
                Iniciar Treinamento
              </Button>
            </CardContent>
          </Card>

          <Card className="border-2 border-primary/30 bg-gradient-to-br from-card via-card to-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BriefcaseBusiness className="w-5 h-5 text-primary" />
                Auxiliar de Venda em Tempo Real
              </CardTitle>
              <CardDescription>
                Acompanhe conversas ao vivo via áudio ou texto e receba diagnósticos instantâneos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/auxiliar-vendas')}
                className="w-full"
                size="lg"
                variant="outline"
              >
                <Mic className="w-4 h-4 mr-2 text-primary" />
                Abrir Auxiliar de Venda
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {personas.length > 0 ? (
            personas.map((persona) => {
              // persona.persona_gerada pode ser um texto livre; mantemos a renderização robusta
              let personaData: any = null;
              if (typeof persona.persona_gerada === 'string') {
                try {
                  personaData = JSON.parse(persona.persona_gerada);
                } catch {
                  personaData = null;
                }
              } else {
                personaData = persona.persona_gerada;
              }

              const title = personaData?.nome || 'Persona';
              const description =
                personaData?.perfil ||
                (typeof persona.persona_gerada === 'string' ? persona.persona_gerada : 'Sem descrição');

              return (
                <Card
                  key={persona.id}
                  className="border-2 border-primary/10 hover:border-primary/30 transition-colors cursor-pointer"
                  onClick={() => {
                    if (!empresaIdForNav) {
                      toast({
                        title: 'Empresa não encontrada',
                        description: 'Tente abrir a persona a partir do painel.',
                        variant: 'destructive',
                      });
                      return;
                    }
                    navigate(`/treinamento?personaId=${persona.id}&empresaId=${empresaIdForNav}`);
                  }}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start gap-2">
                      <CardTitle className="text-lg line-clamp-2">
                        {title}
                      </CardTitle>
                      <div className="flex items-center gap-1 shrink-0">
                        <PersonaScoreBadge score={personaScores[persona.id]} />
                      <AlertDialog
                        onOpenChange={(open) => {
                          // Evita que o click do botão de excluir propague e dispare a navegação
                          if (open === true) {
                            // noop
                          }
                        }}
                      >
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hover:bg-transparent"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Persona</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir esta persona? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePersona(persona.id);
                              }}
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      </div>
                    </div>
                    <CardDescription className="line-clamp-3">
                      {description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground">
                      Criada em {new Date(persona.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="opacity-50 col-span-full">
              <CardHeader>
                <CardTitle className="text-sm">Nenhuma persona criada ainda</CardTitle>
                <CardDescription>Clique em "Iniciar Treinamento" para criar sua primeira persona</CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Vendedor;
